<template>
    <client-only>
        <div v-if="!dontAskAgain && proposal" class="toast toast-start toast-middle lg:toast-end lg:pr-0 z-99">
            <div class="relative w-90 bg-base-200 border flex flex-col items-start gap-6 px-7 py-8 shadow-lg">
                <button class="btn btn-ghost btn-circle absolute top-0 right-0" @click="close()">
                    <span class="sr-only">{{ $t('layout.country.proposal.close') }}</span>
                    <MiscIcon name="x" />
                </button>

                <template v-if="$te(`layout.language.switch.country-${proposal}`)">
                    <div class="w-full text-center font-fjalla-one">
                        {{ $t('layout.country.proposal.headline') }}
                    </div>

                    <div class="w-full flex justify-center gap-20">
                        <button
                            class="flex flex-col gap-3 items-center font-fjalla-one text-xs cursor-pointer"
                            :aria-label="$t('layout.country.proposal.close')"
                            @click="close()"
                        >
                            <MiscIcon
                                :name="`flag-${flag?.toLowerCase()}`"
                                size="2xl"
                                class="inline-flex country-flag"
                                aria-hidden="true"
                            />
                            <span>{{ flag?.toUpperCase() }}</span>
                        </button>

                        <button
                            class="flex flex-col gap-3 items-center font-fjalla-one text-xs cursor-pointer"
                            :aria-label="$t('layout.country.proposal.target', {
                                country: $t(`layout.country.proposal.country-${proposal}`)
                            })"
                            @click="change()"
                        >
                            <MiscIcon
                                :name="`flag-${proposal?.toLowerCase()}`"
                                size="2xl"
                                class="inline-flex country-flag"
                                aria-hidden="true"
                            />
                            <span>{{ proposal?.toUpperCase() }}</span>
                        </button>
                    </div>

                    <div class="w-full text-center text-wrap text-xs">
                        {{ $t('layout.country.proposal.info') }}
                    </div>
                </template>
                <template v-else>
                    <div class="w-full text-center text-wrap text-xs">
                        {{ $t('layout.country.proposal.fallback') }}
                    </div>
                </template>
            </div>
        </div>
    </client-only>
</template>

<script setup lang="ts">
const nuxtApp = useNuxtApp()
const { apiClient } = useShopwareContext()
const locale = nuxtApp.$i18n?.locale?.value
const dontAskAgain = ref(false)
const proposalCookie = useCookie<string | null>('a4g-country-proposal', {
    maxAge: 60 * 60 * 24 * 30, // 30 days
    sameSite: 'lax',
    path: '/',
    decode: value => value ?? null
})
const proposal = ref<null | string>(null)
const {
    salesChannelDomains,
    platformCountries,
    platformDefaults
} = useRuntimeConfig().public

// Watch for programmatic country proposal trigger (e.g., from Apple Pay)
const countryProposalTrigger = useState<string | null>('country-proposal-trigger')
watch(countryProposalTrigger, (newCountry) => {
    if (newCountry) {
        // Set proposal to uppercase to match translation keys (country-DE, not country-de)
        proposal.value = newCountry.toUpperCase()
        dontAskAgain.value = false
        // Reset the trigger
        countryProposalTrigger.value = null
    }
})

if (proposalCookie.value === '1') {
    dontAskAgain.value = true
}

const flag = computed(() => {
    // Workaround to display AT and CH, which are not represented as languages in the shop.
    // `locale` is narrowed per-saleschannel by i18n strict typing, so cast to broaden
    // before comparing across saleschannels.
    if ((locale as string) === 'de' && nuxtApp.$config?.app?.baseURL !== '/') {
        return nuxtApp.$config?.app?.baseURL?.startsWith('/') ? nuxtApp.$config?.app?.baseURL?.slice(1).trim() : 'DE'
    }
    return locale
})

const countryBlacklist: string[] = []

onMounted(async() => {
    if (!dontAskAgain.value) {
        try {
            const defaultCountry = platformCountries.find((country) => {
                return country.id === platformDefaults.countryId
            })?.iso.toLowerCase()

            if (defaultCountry == null) {
                return
            }

            const response = await apiClient.invoke("validateCountry post /geo-service/validate-country", {
                body: {
                    country: defaultCountry
                }
            })

            // country in url doesn't match with IP but could be found
            if (response?.data?.error != null &&
                response?.data?.error === 'Mismatch countries' &&
                response?.data?.country != null &&
                response?.data?.country.toLowerCase() !== defaultCountry &&
                !countryBlacklist.includes(response?.data?.country.toLowerCase())
            ) {
                proposal.value = response?.data?.country
            }
        } catch (e) {
            console.error(e);
        }
    }
})

function close () {
    dontAskAgain.value = true
    proposalCookie.value = '1'
}

function change () {
    const key = proposal?.value?.toLowerCase()
    const domain = key != null ? Object.entries(salesChannelDomains).find(([k]) => k === key)?.[1] : undefined
    if (locale && domain != null) {
        proposalCookie.value = '1'
        window.location.href = `${domain}/`
    }
}
</script>
